import { BlockViewModel } from "./Board";
import { BlockModel } from "./model/BlockModel";
import { LayoutMatrix } from "./common";

export function removeRows(blockViewModels: BlockViewModel[], rows: number[]): BlockViewModel[] {
  return rows.reduce((blockVms, row) => {
    return removeRow(blockVms, row);
  }, blockViewModels).filter((bvm: BlockViewModel) => {
    return bvm.block.layout.length > 0;
  });
}

function removeRow(blockViewModels: BlockViewModel[], row: number): BlockViewModel[] {
  return blockViewModels.map(bvm => {
    const startRow = bvm.y
    const endRow = bvm.y + bvm.block.height() - 1;
    if (row > endRow) {
      return {
        hue: bvm.hue,
        x: bvm.x,
        y: bvm.y + 1,
        block: bvm.block
      };
    } else if (row >= startRow) {
      return normaliseBlockViewModel({
        hue: bvm.hue,
        x: bvm.x,
        y: bvm.y + 1,
        block: removeRowFromBlock(bvm.block, row - startRow)
      });
    } else {
      return bvm;
    }
  });
}

function removeRowFromBlock(block: BlockModel, rowIndex: number): BlockModel {
  const newLayout: LayoutMatrix = [];
  block.layout.forEach((row, index) => {
    if (index !== rowIndex) {
      newLayout.push(row);
    }
  });
  return new BlockModel(newLayout);
}

function normaliseBlockViewModel(blockViewModel: BlockViewModel): BlockViewModel {
  const {hue, x, y, block} = blockViewModel;
  const {offsetX, offsetY, newBlock} = normaliseBlock(block);
  return {
    hue,
    x: x + offsetX,
    y: y + offsetY,
    block: newBlock
  }
}

interface NormalisationResult {
  offsetX: number;
  offsetY: number;
  newBlock: BlockModel;
}

function normaliseBlock(block: BlockModel): NormalisationResult {
  return [
    removeEmptyBottomRow,
    removeEmptyRightColumn,
    removeEmptyTopRow,
    removeEmptyLeftColumn
  ].reduce<NormalisationResult>((accResult: NormalisationResult, func: (block: BlockModel) => NormalisationResult) => {
    const newResult = func(accResult.newBlock);
    return {
      offsetX: accResult.offsetX + newResult.offsetX,
      offsetY: accResult.offsetY + newResult.offsetY,
      newBlock: newResult.newBlock
    };
  }, {offsetX: 0, offsetY: 0, newBlock: block});
}

function removeEmptyBottomRow(block: BlockModel): NormalisationResult {
  const height = block.layout.length;
  let newBlock;
  if (height > 0) {
    const bottomRow = block.layout[height - 1];
    const bottomRowOccupied = bottomRow.reduce((a, b) => (a || b), false);
    if (bottomRowOccupied) {
      newBlock = block;
    } else {
      const newLayout = block.layout.slice(0, block.layout.length - 1)
      newBlock = new BlockModel(newLayout);
    }
  } else {
    newBlock = block;
  }
  return {
    offsetX: 0,
    offsetY: 0,
    newBlock
  };
}

function removeEmptyTopRow(block: BlockModel): NormalisationResult {
  const height = block.layout.length;
  if (height > 0) {
    const topRow = block.layout[0];
    const topRowOccupied = topRow.reduce((a, b) => (a || b), false);
    if (topRowOccupied) {
      return {
        offsetX: 0,
        offsetY: 0,
        newBlock: block
      };
    } else {
      const newLayout = block.layout.slice(1, block.layout.length - 1)
      return {
        offsetX: 0,
        offsetY: 1,
        newBlock: new BlockModel(newLayout)
      };
    }
  } else {
    return {
      offsetX: 0,
      offsetY: 0,
      newBlock: block
    };
  }
}

function removeEmptyRightColumn(block: BlockModel): NormalisationResult {
  const height = block.layout.length;
  let newBlock;
  if (height > 0) {
    const rightColumnIndex = block.layout[0].length - 1;
    const rightColumnOccupied = block.layout.reduce((acc, row) => (row[rightColumnIndex] || acc), false);
    if (rightColumnOccupied) {
      newBlock = block;
    } else {
      const newLayout = [];
      for(let i=0; i<block.layout.length; i+=1) {
        const newRow = block.layout[i].slice(0, block.layout[i].length - 1);
        newLayout.push(newRow);
      }
      newBlock = new BlockModel(newLayout);
    }
  } else {
    newBlock = block;
  }
  return {
    offsetX: 0,
    offsetY: 0,
    newBlock
  };
}

function removeEmptyLeftColumn(block: BlockModel): NormalisationResult {
  const height = block.layout.length;
  if (height > 0) {
    const leftColumnIndex = 0;
    const leftColumnOccupied = block.layout.reduce((acc, row) => (row[leftColumnIndex] || acc), false);
    if (leftColumnOccupied) {
      return {
        offsetX: 0,
        offsetY: 0,
        newBlock: block
      };
    } else {
      const newLayout = [];
      for(let i=0; i<block.layout.length; i+=1) {
        const newRow = block.layout[i].slice(1, block.layout[i].length);
        newLayout.push(newRow);
      }
      return {
        offsetX: 1,
        offsetY: 0,
        newBlock: new BlockModel(newLayout)
      };
    }
  } else {
    return {
      offsetX: 0,
      offsetY: 0,
      newBlock: block
    };
  }
}
